import type { CsvImportStatus } from '@/lib/csv-import/api';
import { ApiError } from '@/lib/errors';
import { apiGet, apiPost } from '@/lib/http/client';
import {
  type CoreMapping,
  type MappingAction,
  type MappingProposal,
  type VendorJob,
  VendorJobFailedError,
  type VendorJobStatus,
  VendorJobTimeoutError,
  waitForVendorJob,
} from '@/lib/vendor-import';

export interface HubspotImportObjectType {
  name: string;
  label: string;
  description?: string;
}

export interface ObjectTypesResponse {
  object_types: HubspotImportObjectType[];
}

export interface MappingProposalsResult {
  proposals: Record<string, MappingProposal[]>;
  truncated: Record<string, boolean>;
  total_records: Record<string, number>;
  core_mappings: Record<string, CoreMapping[]>;
  /**
   * Per-entity-type flag: true when the record scan hit its sampling cap, so
   * `total_records` (and every proposal's populated count) is an approximation
   * over a bounded prefix rather than an exhaustive count.
   */
  sampled: Record<string, boolean>;
}

export type HubspotImportJobErrorCode =
  | 'hubspot_unreachable'
  | 'hubspot_auth'
  | 'no_connection'
  | 'no_importable_types'
  | 'invalid_mappings'
  | 'internal';

export interface HubspotImportJob extends VendorJob<HubspotImportJobErrorCode> {
  kind?: 'import' | 'proposals';
  csv_import_status: string | null;
}

export interface CreateImportResult {
  status: CsvImportStatus;
  warning?: string;
}

export interface HubspotConfirmedMapping {
  raw_key: string;
  entity_type_name: string;
  action: MappingAction;
  target_property_name: string;
  field_type_name: string;
  is_multi: boolean;
}

export interface StartJobResponse {
  job_id: number;
}

export { VendorJobFailedError as HubspotImportJobFailedError };
export { VendorJobTimeoutError as HubspotImportJobTimeoutError };

export type HubspotImportJobStatus = VendorJobStatus;

/** Shape of one entry in a create-import `invalid_mappings` error detail. */
export interface InvalidMappingDetail {
  entity_type_name: string;
  raw_key: string;
  target_property_name: string;
}

export async function getHubspotImportObjectTypes(
  orgId: number,
  signal?: AbortSignal
): Promise<ObjectTypesResponse> {
  return apiGet<ObjectTypesResponse>(
    `/api/orgs/${orgId}/hubspot/import/object-types/`,
    undefined,
    undefined,
    signal
  );
}

export async function getHubspotMappingProposals(
  orgId: number,
  types: string[],
  signal?: AbortSignal
): Promise<MappingProposalsResult> {
  const { job_id } = await apiPost<StartJobResponse>(
    `/api/orgs/${orgId}/hubspot/import/mapping-proposals/`,
    { selected_object_types: types },
    undefined,
    { signal }
  );
  const job = await waitForVendorJob<HubspotImportJob>(
    s => getHubspotImportJob(orgId, job_id, s),
    { signal }
  );
  const result = (job.result ?? {}) as unknown as MappingProposalsResult;
  return {
    proposals: result.proposals ?? {},
    truncated: result.truncated ?? {},
    total_records: result.total_records ?? {},
    core_mappings: result.core_mappings ?? {},
    sampled: result.sampled ?? {},
  };
}

export async function startHubspotImport(
  orgId: number,
  params: {
    selectedObjectTypes: string[];
    confirmedMappings?: HubspotConfirmedMapping[];
  },
  signal?: AbortSignal
): Promise<StartJobResponse> {
  return apiPost<StartJobResponse>(
    `/api/orgs/${orgId}/hubspot/import/imports/`,
    {
      selected_object_types: params.selectedObjectTypes,
      ...(params.confirmedMappings
        ? { confirmed_mappings: params.confirmedMappings }
        : {}),
    },
    undefined,
    { signal }
  );
}

export async function getHubspotImportJob(
  orgId: number,
  jobId: number,
  signal?: AbortSignal
): Promise<HubspotImportJob> {
  return apiGet<HubspotImportJob>(
    `/api/orgs/${orgId}/hubspot/import/jobs/${jobId}/`,
    undefined,
    undefined,
    signal
  );
}

export async function waitForHubspotImportJob(
  orgId: number,
  jobId: number,
  signal?: AbortSignal,
  onStatus?: (status: HubspotImportJobStatus) => void
): Promise<HubspotImportJob> {
  return waitForVendorJob<HubspotImportJob>(
    s => getHubspotImportJob(orgId, jobId, s),
    { signal, onStatus }
  );
}

const JOB_ERROR_COPY: Record<HubspotImportJobErrorCode, string> = {
  hubspot_unreachable:
    "We couldn't reach HubSpot. Check your connection and try again.",
  hubspot_auth:
    'Your HubSpot connection has expired. Reconnect HubSpot and try again.',
  no_connection:
    'No active HubSpot connection was found. Connect HubSpot and try again.',
  no_importable_types:
    'No importable object types were found in your HubSpot account.',
  invalid_mappings:
    'Some mappings are no longer valid. Review them and try again.',
  internal:
    'Something went wrong. Try again, and get in touch if it keeps happening.',
};

export function describeHubspotImportError(err: unknown): string {
  if (err instanceof DOMException && err.name === 'AbortError') {
    return 'Import cancelled.';
  }
  if (err instanceof VendorJobFailedError) {
    return (
      JOB_ERROR_COPY[err.code as HubspotImportJobErrorCode] ??
      JOB_ERROR_COPY.internal
    );
  }
  if (err instanceof VendorJobTimeoutError) {
    return "This is taking longer than expected. It's still running – check the imports list in a few minutes.";
  }
  if (err instanceof ApiError) {
    if (err.isForbidden()) {
      return 'HubSpot import is managed by your workspace admins. Ask an admin to enable it.';
    }
    switch (err.statusCode) {
      case 502:
        return "We couldn't reach HubSpot. Check your connection and try again.";
      case 400:
        return "We couldn't complete that request. Double-check your selections, then try again.";
      case 404:
        return "HubSpot import isn't available for this workspace.";
      default:
        return 'Something went wrong. Try again, and get in touch if it keeps happening.';
    }
  }
  return 'Something went wrong. Try again, and get in touch if it keeps happening.';
}

function extractInvalidMappings(response: unknown): InvalidMappingDetail[] {
  if (
    !response ||
    typeof response !== 'object' ||
    !Array.isArray((response as Record<string, unknown>).invalid_mappings)
  ) {
    return [];
  }
  return (
    (response as Record<string, unknown>).invalid_mappings as unknown[]
  ).filter((entry): entry is InvalidMappingDetail => {
    if (typeof entry !== 'object' || entry === null) {
      return false;
    }
    const candidate = entry as Record<string, unknown>;
    return (
      typeof candidate.entity_type_name === 'string' &&
      typeof candidate.raw_key === 'string' &&
      typeof candidate.target_property_name === 'string'
    );
  });
}

/**
 * Describes a create-import failure. When the backend's failed job carries an
 * `invalid_mappings` error detail, names the offending rows so the user knows
 * what to fix. Falls back to describeHubspotImportError's generic copy otherwise.
 */
export function describeConfirmError(err: unknown): string {
  if (err instanceof VendorJobFailedError && err.code === 'invalid_mappings') {
    const invalidMappings = extractInvalidMappings(err.detail);
    if (invalidMappings.length > 0) {
      const rows = invalidMappings
        .map(m => `"${m.raw_key}" (${m.entity_type_name})`)
        .join(', ');
      return `These mappings are no longer valid: ${rows}. Review them and try again.`;
    }
  }
  if (err instanceof ApiError && err.statusCode === 400) {
    const invalidMappings = extractInvalidMappings(err.response);
    if (invalidMappings.length > 0) {
      const rows = invalidMappings
        .map(m => `"${m.raw_key}" (${m.entity_type_name})`)
        .join(', ');
      return `These mappings are no longer valid: ${rows}. Review them and try again.`;
    }
  }
  return describeHubspotImportError(err);
}
